/**
 * OnboardingTaskItem — single row in the onboarding / offboarding checklist.
 *
 * Feb 2026 R138 — v2 · adds inline editing, approver/reviewer sign-offs,
 * per-task due-date + overdue indicator, and delete for custom tasks. Used
 * by both HrOnboarding (JourneyDialog) and OffboardingDialog so both flows
 * behave identically.
 *
 * Props:
 *   task          – task doc from the backend
 *   members       – workspace members `[{user_id, user_name}]` for pickers
 *   kind          – "onboarding" | "offboarding"  (drives the API base path)
 *   currentUserId – for showing "you can sign off" hint
 *   onChanged     – callback fired after any mutation completes
 */
import { useMemo, useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { Check, Pencil, Trash2, ShieldCheck, Eye, EyeOff, AlertTriangle, X, MessageSquare, Circle, Zap, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const OWNER_ROLES = ["hr", "manager", "buddy", "it", "finance", "admin", "employee"];
const OFFBOARDING_CATEGORIES = ["notice", "handover", "clearance", "farewell", "final_pay", "alumni"];
const ONBOARDING_CATEGORIES = ["pre_boarding", "day_0", "day_7", "day_30", "day_60", "day_90"];

/**
 * TaskListHeader — Feb 2026 R138f · shared column header that mirrors
 * the 9-column grid used by `OnboardingTaskItem`. Kept here so both the
 * onboarding and offboarding dialogs render an identical table header
 * on top of every category section.
 */
export function TaskListHeader({ selectable = true }) {
  return (
    <div
      className="hidden md:grid grid-cols-[auto_auto_minmax(220px,1.6fr)_88px_86px_minmax(120px,1fr)_minmax(120px,1fr)_minmax(180px,1.1fr)_auto] gap-2 px-3 py-1.5 border-b border-border bg-muted/30 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold"
      data-testid="task-list-header"
    >
      {selectable ? <span /> : <span />}
      <span title="Owner marks done" className="text-center">Done</span>
      <span>Task</span>
      <span className="text-center">Owner</span>
      <span>Due</span>
      <span>Reviewer</span>
      <span>Approver</span>
      <span>Progress</span>
      <span className="text-right pr-1">Actions</span>
    </div>
  );
}



export function OnboardingTaskItem({ task, members = [], kind = "onboarding", currentUserId,
                                     onChanged,
                                     selectable = false, selected = false, onToggleSelect }) {
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(task.comment_count);

  const patchUrl = kind === "onboarding"
    ? `/onboarding/tasks/${task.id}`
    : `/offboarding/tasks/${task.id}`;
  const signOffUrl = `/onboarding/tasks/${task.id}/sign-off`;   // shared for both

  const today = new Date().toISOString().slice(0, 10);
  const overallStatus = task.overall_status || task.status;
  const isFullyDone = task.fully_complete || overallStatus === "done";
  const overdue = task.due_date && task.due_date < today && overallStatus !== "done" && overallStatus !== "skipped";
  const canDelete = !!task.is_custom;

  const patch = async (fields) => {
    setBusy(true);
    try {
      await api.patch(patchUrl, fields);
      await onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
    finally { setBusy(false); }
  };
  const toggleDone = () => {
    // Feb 2026 R138e — Flow reversed: task owner marks done FIRST, then
    // reviewer signs, then approver signs. No blocking — the mini timeline
    // rendered on the row shows what has happened + what's next.
    patch({ status: task.status === "done" ? "pending" : "done" });
  };
  const signOff = async (role, action = "sign") => {
    setBusy(true);
    try {
      await api.post(signOffUrl, { role, action });
      toast.success(action === "sign" ? `${role} signed off` : `${role} sign-off revoked`);
      await onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Sign-off failed")); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete “${task.title}”? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/onboarding/tasks/${task.id}`);
      toast.success("Task deleted");
      await onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setBusy(false); }
  };

  const approverName = useMemo(() => (
    members.find((m) => (m.user_id || m.id) === task.approver_user_id)?.user_name
      || members.find((m) => (m.user_id || m.id) === task.approver_user_id)?.name
      || (task.approver_user_id ? task.approver_user_id.slice(0, 6) : null)
  ), [members, task.approver_user_id]);
  const reviewerName = useMemo(() => (
    members.find((m) => (m.user_id || m.id) === task.reviewer_user_id)?.user_name
      || members.find((m) => (m.user_id || m.id) === task.reviewer_user_id)?.name
      || (task.reviewer_user_id ? task.reviewer_user_id.slice(0, 6) : null)
  ), [members, task.reviewer_user_id]);
  const assigneeName = useMemo(() => (
    members.find((m) => (m.user_id || m.id) === task.assignee_user_id)?.user_name
      || members.find((m) => (m.user_id || m.id) === task.assignee_user_id)?.name
      || (task.assignee_user_id ? task.assignee_user_id.slice(0, 6) : null)
  ), [members, task.assignee_user_id]);

  return (
    <div
      className={`grid grid-cols-[auto_auto_minmax(0,1fr)_auto] md:grid-cols-[auto_auto_minmax(220px,1.6fr)_88px_86px_minmax(120px,1fr)_minmax(120px,1fr)_minmax(180px,1.1fr)_auto] gap-2 px-3 py-2 border-b border-border last:border-0 text-sm items-start hover:bg-muted/10 ${overdue ? "bg-rose-500/[0.04]" : ""} ${selected ? "bg-primary/[0.06]" : ""}`}
      data-testid={`task-${task.id}`}
    >
      {selectable ? (
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect?.(task.id)}
          className="mt-1 flex-shrink-0"
          data-testid={`task-select-${task.id}`}
          onClick={(e) => e.stopPropagation()}
        />
      ) : <span />}
      <button
        className={`w-4 h-4 mt-0.5 border-2 flex items-center justify-center flex-shrink-0 ${task.status === "done" ? "bg-emerald-500 border-emerald-500 text-white" : task.status === "skipped" ? "bg-muted border-border" : "border-border hover:border-primary"} ${busy ? "opacity-50" : ""}`}
        onClick={toggleDone}
        disabled={busy}
        title={task.status === "done" ? "Mark pending" : "Mark done"}
        data-testid={`task-check-${task.id}`}
      >
        {task.status === "done" && <Check className="w-3 h-3" />}
      </button>

      {/* Title + description + badges */}
      <div className="min-w-0">
        <div className={`text-sm flex items-center gap-1.5 flex-wrap ${task.status === "done" ? "line-through text-muted-foreground" : ""}`}>
          <span className="truncate">{task.title}</span>
          {task.is_custom && (
            <span className="text-[9px] uppercase tracking-widest bg-primary/10 text-primary px-1 border border-primary/30" data-testid={`task-badge-custom-${task.id}`}>custom</span>
          )}
          {task.is_required && (
            <span className="text-[9px] uppercase tracking-widest bg-amber-500/10 text-amber-700 px-1 border border-amber-500/30">required</span>
          )}
          {task.is_visible_to_employee && (
            <span className="text-[9px] uppercase tracking-widest bg-sky-500/10 text-sky-700 px-1 border border-sky-500/30" title="Visible to employee" data-testid={`task-badge-visible-${task.id}`}>visible</span>
          )}
        </div>
        {task.description && <div className="text-[11px] text-muted-foreground line-clamp-2">{task.description}</div>}
        <TaskProgressTimeline task={task} />
        {/* Mobile-only meta: owner + due + review + approve compressed */}
        <div className="md:hidden mt-1.5 flex items-center gap-1.5 flex-wrap text-[10px]">
          <span className={`border px-1.5 py-0.5 ${task.assignee_user_id ? "border-primary/40 bg-primary/5 text-primary" : "border-border text-muted-foreground"}`}
                 data-testid={`task-owner-mobile-${task.id}`}>
            {task.assignee_user_id ? assigneeName : task.owner_role}
          </span>
          <span className={`font-mono ${overdue ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}
                 data-testid={`task-due-mobile-${task.id}`}>
            {overdue && "⚠ "}{task.due_date || "—"}
          </span>
          {task.reviewer_user_id && (
            <span className={`border px-1.5 py-0.5 ${task.reviewer_signed_at ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : "bg-muted/40 border-border"}`}>
              R: {reviewerName}{task.reviewer_signed_at ? " ✓" : ""}
            </span>
          )}
          {task.approver_user_id && (
            <span className={`border px-1.5 py-0.5 ${task.approver_signed_at ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : "bg-muted/40 border-border"}`}>
              A: {approverName}{task.approver_signed_at ? " ✓" : ""}
            </span>
          )}
        </div>
      </div>

      {/* Owner (specific user if assigned, else role slug) — desktop only */}
      <span
        className={`hidden md:inline text-[10px] border px-1.5 py-0.5 capitalize self-start text-center truncate ${task.assignee_user_id ? "border-primary/40 bg-primary/5 text-primary font-semibold" : "border-border text-muted-foreground"}`}
        title={task.assignee_user_id ? `Assigned to ${assigneeName}` : `Owner role: ${task.owner_role}`}
        data-testid={`task-owner-${task.id}`}
      >
        {task.assignee_user_id ? assigneeName : task.owner_role}
      </span>

      {/* Due — desktop only */}
      <span className={`hidden md:flex text-[10px] font-mono self-start items-center gap-1 ${overdue ? "text-rose-600 font-semibold" : "text-muted-foreground"}`} data-testid={`task-due-${task.id}`}>
        {overdue && <AlertTriangle className="w-3 h-3" />}
        {task.due_date || "—"}
      </span>

      {/* Reviewer — desktop only */}
      <div className="hidden md:block self-start min-w-0">
        {task.reviewer_user_id ? (
          <button
            onClick={() => signOff("reviewer", task.reviewer_signed_at ? "revoke" : "sign")}
            disabled={busy}
            className={`chip w-full text-[10px] flex items-center gap-1 border px-1.5 py-0.5 ${task.reviewer_signed_at ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : "bg-muted/40 border-border hover:border-primary"}`}
            title={task.reviewer_signed_at ? `Signed ${task.reviewer_signed_at.slice(0, 10)} — click to revoke` : "Click to sign as reviewer"}
            data-testid={`task-review-${task.id}`}
          >
            <Eye className="w-2.5 h-2.5 shrink-0" />
            <span className="truncate">{reviewerName}</span>
            {task.reviewer_signed_at && <Check className="w-2.5 h-2.5 shrink-0" />}
          </button>
        ) : <span className="text-[10px] text-muted-foreground italic">—</span>}
      </div>

      {/* Approver — desktop only */}
      <div className="hidden md:block self-start min-w-0">
        {task.approver_user_id ? (
          <button
            onClick={() => signOff("approver", task.approver_signed_at ? "revoke" : "sign")}
            disabled={busy}
            className={`chip w-full text-[10px] flex items-center gap-1 border px-1.5 py-0.5 ${task.approver_signed_at ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : "bg-muted/40 border-border hover:border-primary"}`}
            title={task.approver_signed_at ? `Signed ${task.approver_signed_at.slice(0, 10)} — click to revoke` : "Click to sign as approver"}
            data-testid={`task-approve-${task.id}`}
          >
            <ShieldCheck className="w-2.5 h-2.5 shrink-0" />
            <span className="truncate">{approverName}</span>
            {task.approver_signed_at && <Check className="w-2.5 h-2.5 shrink-0" />}
          </button>
        ) : <span className="text-[10px] text-muted-foreground italic">—</span>}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 self-start justify-end">
        {/* Feb 2026 R138c — quick toggle: visible to employee */}
        <button
          className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${task.is_visible_to_employee ? "text-sky-600 hover:text-sky-800" : "text-muted-foreground/60 hover:text-primary"} hover:bg-muted/40`}
          onClick={() => patch({ is_visible_to_employee: !task.is_visible_to_employee })}
          disabled={busy}
          title={task.is_visible_to_employee ? "Visible to employee — click to hide" : "Hidden from employee — click to show"}
          data-testid={`task-toggle-visible-${task.id}`}
        >
          {task.is_visible_to_employee ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
        </button>

        {/* Comments trigger */}
        <Popover open={commentsOpen} onOpenChange={setCommentsOpen}>
          <PopoverTrigger asChild>
            <button
              className="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted/40 flex-shrink-0 relative"
              title="Comments"
              data-testid={`task-comments-${task.id}`}
            >
              <MessageSquare className="w-3 h-3" />
              {commentCount > 0 && (
                <span className="absolute -top-1 -right-1 text-[8px] bg-primary text-primary-foreground rounded-full w-3 h-3 flex items-center justify-center leading-none font-semibold">
                  {commentCount}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-96 rounded-none p-0" side="left" align="start" data-testid={`task-comments-popover-${task.id}`}>
            <TaskCommentsPanel
              taskId={task.id}
              taskTitle={task.title}
              onCountChange={setCommentCount}
              onClose={() => setCommentsOpen(false)}
            />
          </PopoverContent>
        </Popover>

        <Popover open={editOpen} onOpenChange={setEditOpen}>
          <PopoverTrigger asChild>
            <button
              className="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted/40 flex-shrink-0"
              title="Edit task"
              data-testid={`task-edit-${task.id}`}
            >
              <Pencil className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 rounded-none p-0" side="left" align="start" data-testid={`task-edit-popover-${task.id}`}>
            <TaskEditor
              task={task}
              kind={kind}
              members={members}
              onSave={async (patchPayload) => {
                await patch(patchPayload);
                setEditOpen(false);
              }}
              onClose={() => setEditOpen(false)}
            />
          </PopoverContent>
        </Popover>

        {!isFullyDone && (
          <button
            className="w-6 h-6 flex items-center justify-center text-amber-600 hover:bg-amber-500/10 flex-shrink-0"
            onClick={async () => {
              const note = window.prompt("Force-complete this task and sign off on behalf of the reviewer + approver? Optionally add an audit note:", "");
              if (note === null) return;
              setBusy(true);
              try {
                await api.post(`/onboarding/tasks/${task.id}/force-complete`, { note });
                toast.success("Task force-completed (admin override)");
                await onChanged?.();
              } catch (e) { toast.error(apiErrorMessage(e, "Force-complete failed")); }
              finally { setBusy(false); }
            }}
            disabled={busy}
            title="Admin override — mark this task fully complete"
            data-testid={`task-force-${task.id}`}
          >
            <Zap className="w-3 h-3" />
          </button>
        )}
        {canDelete && (
          <button
            className="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-rose-600 hover:bg-rose-500/10 flex-shrink-0"
            onClick={remove}
            disabled={busy}
            title="Delete custom task"
            data-testid={`task-delete-${task.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * TaskProgressTimeline — Feb 2026 R138e · a small stage tracker under
 * each task showing which of Owner → Reviewer → Approver has completed
 * their part, with an ISO date under each stamp so HR can see the flow
 * at a glance. Skips stages that aren't configured on the task.
 */
function TaskProgressTimeline({ task }) {
  const stages = [];
  stages.push({
    key: "owner",
    label: "Owner",
    done: task.status === "done",
    at: task.completed_at,
  });
  if (task.reviewer_user_id) {
    stages.push({
      key: "reviewer",
      label: "Reviewed",
      done: !!task.reviewer_signed_at,
      at: task.reviewer_signed_at,
    });
  }
  if (task.approver_user_id) {
    stages.push({
      key: "approver",
      label: "Approved",
      done: !!task.approver_signed_at,
      at: task.approver_signed_at,
    });
  }
  if (stages.length === 1 && !stages[0].done) return null;   // nothing meaningful to show
  return (
    <div className="flex items-center gap-0.5 mt-1 text-[10px] font-mono" data-testid={`task-timeline-${task.id}`}>
      {stages.map((s, i) => (
        <span key={s.key} className="flex items-center gap-0.5">
          {i > 0 && <span className={`mx-0.5 ${s.done || stages[i - 1].done ? "text-emerald-600" : "text-muted-foreground/40"}`}>→</span>}
          <span
            className={`inline-flex items-center gap-0.5 px-1 border ${s.done ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : "text-muted-foreground/60 border-border"}`}
            title={s.done && s.at ? `${s.label} ${s.at.slice(0, 16).replace("T", " ")}` : `${s.label} · pending`}
          >
            {s.done ? <Check className="w-2 h-2" /> : <Circle className="w-2 h-2" />}
            {s.label}
            {s.done && s.at && <span className="opacity-70">·{s.at.slice(5, 10)}</span>}
          </span>
        </span>
      ))}
    </div>
  );
}

function TaskEditor({ task, kind, members, onSave, onClose }) {
  const [title, setTitle] = useState(task.title || "");
  const [description, setDescription] = useState(task.description || "");
  const [ownerRole, setOwnerRole] = useState(task.owner_role || "hr");
  const [category, setCategory] = useState(task.category || (kind === "onboarding" ? "day_0" : "handover"));
  const [dueDate, setDueDate] = useState(task.due_date || "");
  const [approver, setApprover] = useState(task.approver_user_id || "");
  const [reviewer, setReviewer] = useState(task.reviewer_user_id || "");
  const [assignee, setAssignee] = useState(task.assignee_user_id || "");
  const [isRequired, setIsRequired] = useState(!!task.is_required);
  const [isVisible, setIsVisible] = useState(!!task.is_visible_to_employee);
  const [saving, setSaving] = useState(false);

  const categories = kind === "onboarding" ? ONBOARDING_CATEGORIES : OFFBOARDING_CATEGORIES;
  const memberList = members.map((m) => ({
    id: m.user_id || m.id,
    name: m.user_name || m.name || m.email,
  }));

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim(),
        owner_role: ownerRole,
        category,
        due_date: dueDate || null,
        approver_user_id: approver || null,
        reviewer_user_id: reviewer || null,
        assignee_user_id: assignee || null,
        is_required: isRequired,
        is_visible_to_employee: isVisible,
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="border-2 border-primary" data-testid="task-editor">
      <div className="px-3 py-2 border-b border-border bg-muted/40 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest">Edit task</div>
        <button className="text-muted-foreground hover:text-foreground" onClick={onClose}><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="p-3 space-y-2 max-h-[70vh] overflow-auto">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Title</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-none h-8 text-sm" data-testid="task-editor-title" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Description</label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="rounded-none text-sm resize-none" data-testid="task-editor-desc" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Owner role</label>
            <Select value={ownerRole} onValueChange={setOwnerRole}>
              <SelectTrigger className="rounded-none h-8 text-xs" data-testid="task-editor-owner"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OWNER_ROLES.map((r) => <SelectItem key={r} value={r} className="text-xs capitalize">{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-none h-8 text-xs" data-testid="task-editor-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c} className="text-xs">{c.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Due date</label>
          <Input type="date" value={dueDate?.slice(0, 10) || ""} onChange={(e) => setDueDate(e.target.value)} className="rounded-none h-8 text-sm" data-testid="task-editor-due" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Assignee (any workspace user)</label>
          <SearchableSelect
            value={assignee}
            onValueChange={(v) => setAssignee(v || "")}
            options={[{ value: "", label: "— none (use owner role) —" }, ...memberList.map((m) => ({ value: m.id, label: m.name }))]}
            placeholder="Pick a specific person"
            clearable
            triggerClassName="rounded-none h-8 text-xs"
            data-testid="task-editor-assignee"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Reviewer</label>
          <SearchableSelect
            value={reviewer}
            onValueChange={(v) => setReviewer(v || "")}
            options={[{ value: "", label: "— none —" }, ...memberList.map((m) => ({ value: m.id, label: m.name }))]}
            placeholder="Select reviewer"
            clearable
            triggerClassName="rounded-none h-8 text-xs"
            data-testid="task-editor-reviewer"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Approver</label>
          <SearchableSelect
            value={approver}
            onValueChange={(v) => setApprover(v || "")}
            options={[{ value: "", label: "— none —" }, ...memberList.map((m) => ({ value: m.id, label: m.name }))]}
            placeholder="Select approver"
            clearable
            triggerClassName="rounded-none h-8 text-xs"
            data-testid="task-editor-approver"
          />
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} data-testid="task-editor-required" />
          Required to complete onboarding
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={isVisible} onChange={(e) => setIsVisible(e.target.checked)} data-testid="task-editor-visible" />
          Visible to the employee (they can mark status + comment)
        </label>
      </div>
      <div className="px-3 py-2 border-t border-border flex items-center gap-2 justify-end">
        <Button variant="ghost" size="sm" className="rounded-none h-7" onClick={onClose}>Cancel</Button>
        <Button size="sm" className="rounded-none h-7" onClick={save} disabled={saving || !title.trim()} data-testid="task-editor-save">
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}


/**
 * AddCustomTaskButton — the "+ Add task" affordance shown next to each
 * category header. Opens a lightweight editor pre-scoped to the category.
 */
export function AddCustomTaskButton({ jid, kind = "onboarding", category, members = [], onCreated }) {
  const [open, setOpen] = useState(false);
  const create = async (payload) => {
    const url = kind === "onboarding"
      ? `/onboarding/journeys/${jid}/tasks`
      : `/offboarding/journeys/${jid}/tasks`;
    try {
      await api.post(url, { ...payload, category });
      toast.success("Task added");
      await onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); throw e; }
  };
  return (
    <>
      <button
        className="text-[10px] uppercase tracking-widest text-primary hover:underline flex items-center gap-1"
        onClick={() => setOpen(true)}
        data-testid={`add-task-btn-${category}`}
      >
        + Add task
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="z-[130] max-w-md rounded-none p-0" data-testid={`add-task-modal-${category}`}>
          <DialogTitle className="sr-only">Add task · {category.replace(/_/g, " ")}</DialogTitle>
          <NewTaskEditor
            kind={kind}
            category={category}
            members={members}
            onCreate={async (payload) => { await create(payload); setOpen(false); }}
            onClose={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * BulkAddTasksButton — Feb 2026 · Paste multiple task titles (one per line)
 * and pick shared owner_role / due_date / required + visible flags once.
 * Fires N sequential POSTs to `/onboarding/journeys/{jid}/tasks` (or the
 * offboarding equivalent) with the shared payload + per-line title. Mirrors
 * the R170 template bulk-add UX but for an in-flight journey.
 */
export function BulkAddTasksButton({ jid, kind = "onboarding", category, members = [], onCreated }) {
  const [open, setOpen] = useState(false);
  const [rawTitles, setRawTitles] = useState("");
  const [shared, setShared] = useState({
    owner_role: "hr",
    due_date: "",
    is_required: false,
    is_visible_to_employee: false,
  });
  const [saving, setSaving] = useState(false);
  const titles = useMemo(
    () => rawTitles.split("\n").map((l) => l.trim()).filter(Boolean),
    [rawTitles],
  );
  const url = kind === "onboarding"
    ? `/onboarding/journeys/${jid}/tasks`
    : `/offboarding/journeys/${jid}/tasks`;
  const reset = () => { setRawTitles(""); setShared((s) => ({ ...s, due_date: "" })); };
  const submit = async () => {
    if (titles.length === 0) return;
    setSaving(true);
    let ok = 0, fail = 0;
    for (const title of titles) {
      try {
        await api.post(url, {
          title,
          description: "",
          owner_role: shared.owner_role,
          category,
          due_date: shared.due_date || null,
          is_required: shared.is_required,
          is_visible_to_employee: shared.is_visible_to_employee,
        });
        ok += 1;
      } catch (e) {
        fail += 1;
        // Toast individual failures but keep going so the batch survives
        // a single bad title.
        toast.error(apiErrorMessage(e, `Failed on "${title}"`));
      }
    }
    setSaving(false);
    if (ok > 0) toast.success(`${ok} task${ok === 1 ? "" : "s"} added${fail ? ` · ${fail} failed` : ""}`);
    if (ok > 0) { reset(); setOpen(false); await onCreated?.(); }
  };
  return (
    <>
      <button
        className="text-[10px] uppercase tracking-widest text-primary hover:underline flex items-center gap-1"
        onClick={() => setOpen(true)}
        data-testid={`bulk-add-btn-${category}`}
      >
        + Bulk add
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="z-[130] max-w-lg rounded-none p-0" data-testid={`bulk-add-modal-${category}`}>
          <DialogTitle className="sr-only">Bulk add tasks · {category.replace(/_/g, " ")}</DialogTitle>
          <div className="border-2 border-primary">
            <div className="px-3 py-2 border-b border-border bg-primary/10 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest">
                Bulk add tasks · <span className="capitalize">{category.replace(/_/g, " ")}</span>
              </div>
              <button className="text-muted-foreground hover:text-foreground" onClick={() => setOpen(false)}><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="p-3 space-y-3 max-h-[70vh] overflow-auto">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Titles · one per line</label>
                <Textarea
                  value={rawTitles}
                  onChange={(e) => setRawTitles(e.target.value)}
                  placeholder={"Set up laptop\nCreate email account\nAdd to team Slack"}
                  rows={7}
                  className="rounded-none text-sm resize-none font-mono"
                  autoFocus
                  data-testid="bulk-add-titles"
                />
                <div className="text-[10px] text-muted-foreground mt-1">
                  {titles.length} task{titles.length === 1 ? "" : "s"} detected · all inherit the shared values below
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Owner role</label>
                  <Select value={shared.owner_role} onValueChange={(v) => setShared((s) => ({ ...s, owner_role: v }))}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="bulk-add-owner"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {OWNER_ROLES.map((r) => <SelectItem key={r} value={r} className="text-xs capitalize">{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Due date (optional)</label>
                  <Input
                    type="date"
                    value={shared.due_date}
                    onChange={(e) => setShared((s) => ({ ...s, due_date: e.target.value }))}
                    className="rounded-none h-8 text-sm"
                    data-testid="bulk-add-due"
                  />
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shared.is_required}
                    onChange={(e) => setShared((s) => ({ ...s, is_required: e.target.checked }))}
                    data-testid="bulk-add-required"
                  />
                  Required
                </label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shared.is_visible_to_employee}
                    onChange={(e) => setShared((s) => ({ ...s, is_visible_to_employee: e.target.checked }))}
                    data-testid="bulk-add-visible"
                  />
                  Visible to employee
                </label>
              </div>
            </div>
            <div className="border-t border-border px-3 py-2 flex justify-end gap-2">
              <Button variant="ghost" className="rounded-none h-8" onClick={() => setOpen(false)}>
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button
                className="rounded-none h-8"
                disabled={saving || titles.length === 0}
                onClick={submit}
                data-testid="bulk-add-submit"
              >
                {saving ? "Adding…" : `Add ${titles.length || ""} task${titles.length === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * ImportFromTemplateButton — R268xxx · Feb 2026
 * On a live journey, lets HR pick a workspace Onboarding/Offboarding
 * template and import that template's tasks for the current category
 * with one click. Reuses the same `POST /(on|off)boarding/journeys/{jid}/tasks`
 * endpoint the single-add uses — no new backend endpoint.
 */
export function ImportFromTemplateButton({ jid, wsId, kind = "onboarding", category, onCreated }) {
  const [open, setOpen] = useState(false);
  const [tpls, setTpls] = useState([]);
  const [tplId, setTplId] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({}); // idx -> bool
  const [saving, setSaving] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [keepDefaults, setKeepDefaults] = useState(true);
  const [markRequired, setMarkRequired] = useState(false);
  const [visibleOverride, setVisibleOverride] = useState(false);

  const listUrl = kind === "onboarding"
    ? `/workspaces/${wsId}/onboarding/templates`
    : `/workspaces/${wsId}/offboarding/templates`;
  const createUrl = kind === "onboarding"
    ? `/onboarding/journeys/${jid}/tasks`
    : `/offboarding/journeys/${jid}/tasks`;

  const loadTemplates = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      const list = Array.isArray(data) ? data : [];
      setTpls(list);
      // Prefer the default template if we have one
      const preferred = list.find((t) => t.is_default) || list[0];
      if (preferred) setTplId(preferred.id);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not load templates"));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) loadTemplates(); /* eslint-disable-next-line */ }, [open]);

  const tpl = useMemo(() => tpls.find((t) => t.id === tplId) || null, [tpls, tplId]);
  const eligible = useMemo(
    () => (tpl?.tasks || []).map((t, idx) => ({ ...t, __idx: idx })).filter((t) => (t.category || "") === category),
    [tpl, category],
  );
  // Every eligible task is pre-selected whenever the template changes.
  useEffect(() => {
    const next = {};
    eligible.forEach((t) => { next[t.__idx] = true; });
    setSelected(next);
  }, [tplId, eligible.length]);  // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = eligible.filter((t) => selected[t.__idx]);
  const allChecked = chosen.length === eligible.length && eligible.length > 0;
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else { const n = {}; eligible.forEach((t) => { n[t.__idx] = true; }); setSelected(n); }
  };
  const reset = () => { setDueDate(""); setKeepDefaults(true); setMarkRequired(false); setVisibleOverride(false); };
  const submit = async () => {
    if (chosen.length === 0) return;
    setSaving(true);
    let ok = 0, fail = 0;
    for (const t of chosen) {
      try {
        await api.post(createUrl, {
          title: (t.title || "").trim() || "Untitled",
          description: t.description || "",
          owner_role: t.owner_role || "hr",
          category,
          due_date: dueDate || null,
          is_required: keepDefaults ? !!t.is_required : markRequired,
          is_visible_to_employee: keepDefaults ? !!t.is_visible_to_employee : visibleOverride,
        });
        ok += 1;
      } catch (e) {
        fail += 1;
        toast.error(apiErrorMessage(e, `Failed on "${t.title}"`));
      }
    }
    setSaving(false);
    if (ok > 0) toast.success(`${ok} task${ok === 1 ? "" : "s"} imported${fail ? ` · ${fail} failed` : ""}`);
    if (ok > 0) { setOpen(false); reset(); await onCreated?.(); }
  };

  return (
    <>
      <button
        className="text-[10px] uppercase tracking-widest text-primary hover:underline flex items-center gap-1"
        onClick={() => setOpen(true)}
        data-testid={`import-tpl-btn-${category}`}
      >
        + From template
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="z-[130] max-w-xl rounded-none p-0" data-testid={`import-tpl-modal-${category}`}>
          <DialogTitle className="sr-only">Import tasks from template · {category.replace(/_/g, " ")}</DialogTitle>
          <DialogDescription className="sr-only">Pick a template and choose which tasks from that template should be added to this journey.</DialogDescription>
          <div className="border-2 border-primary">
            <div className="px-3 py-2 border-b border-border bg-primary/10 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest">
                Import from template · <span className="capitalize">{category.replace(/_/g, " ")}</span>
              </div>
              <button className="text-muted-foreground hover:text-foreground" onClick={() => setOpen(false)}><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="p-3 space-y-3 max-h-[70vh] overflow-auto">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Template</label>
                <Select value={tplId} onValueChange={setTplId} disabled={loading || tpls.length === 0}>
                  <SelectTrigger className="rounded-none h-8 text-xs" data-testid="import-tpl-select">
                    <SelectValue placeholder={loading ? "Loading…" : "Pick a template"} />
                  </SelectTrigger>
                  <SelectContent className="z-[140]">
                    {tpls.map((t) => (
                      <SelectItem key={t.id} value={t.id} className="text-xs">
                        {t.name || "Untitled"}{t.is_default ? " · default" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {tplId && (
                <div>
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                    <span>Tasks in this category · {eligible.length}</span>
                    {eligible.length > 0 && (
                      <button className="text-primary hover:underline" onClick={toggleAll} data-testid="import-tpl-toggle-all">
                        {allChecked ? "Clear all" : "Select all"}
                      </button>
                    )}
                  </div>
                  {eligible.length === 0 ? (
                    <div className="text-xs text-muted-foreground border border-dashed border-border p-3">
                      This template has no tasks for “{category.replace(/_/g, " ")}”.
                    </div>
                  ) : (
                    <div className="border border-border divide-y divide-border max-h-56 overflow-auto">
                      {eligible.map((t) => (
                        <label key={t.__idx} className="flex items-start gap-2 p-2 text-xs cursor-pointer hover:bg-muted/20" data-testid={`import-tpl-row-${t.__idx}`}>
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={!!selected[t.__idx]}
                            onChange={(e) => setSelected((s) => ({ ...s, [t.__idx]: e.target.checked }))}
                            data-testid={`import-tpl-check-${t.__idx}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{t.title || "Untitled"}</div>
                            <div className="text-muted-foreground text-[11px] flex flex-wrap gap-x-2 gap-y-0.5">
                              <span className="capitalize">Owner: {t.owner_role || "hr"}</span>
                              {typeof t.offset_days === "number" && <span>· +{t.offset_days}d</span>}
                              {t.is_required && <span className="text-primary uppercase tracking-widest">Required</span>}
                              {t.is_visible_to_employee && <span className="text-emerald-600 uppercase tracking-widest">Visible</span>}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Due date (optional)</label>
                  <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded-none h-8 text-sm" data-testid="import-tpl-due" />
                </div>
                <div className="text-xs">
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground block">Required / Visible</label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer mt-1">
                    <input type="checkbox" checked={keepDefaults} onChange={(e) => setKeepDefaults(e.target.checked)} data-testid="import-tpl-keep-defaults" />
                    Keep template defaults
                  </label>
                </div>
              </div>
              {!keepDefaults && (
                <div className="flex items-center gap-4 text-xs pl-1">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={markRequired} onChange={(e) => setMarkRequired(e.target.checked)} data-testid="import-tpl-required" />
                    Mark all as Required
                  </label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={visibleOverride} onChange={(e) => setVisibleOverride(e.target.checked)} data-testid="import-tpl-visible" />
                    Visible to employee
                  </label>
                </div>
              )}
            </div>
            <div className="border-t border-border px-3 py-2 flex justify-end gap-2">
              <Button variant="ghost" className="rounded-none h-8" onClick={() => setOpen(false)}>
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button
                className="rounded-none h-8"
                disabled={saving || chosen.length === 0}
                onClick={submit}
                data-testid="import-tpl-submit"
              >
                {saving ? "Importing…" : `Import ${chosen.length || ""} task${chosen.length === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}



/**
 * SortableTaskList — R268xxx · Feb 2026
 * Drag-to-reorder within a single category. Wraps the list of `OnboardingTaskItem`s
 * with `@dnd-kit`. The drag handle is rendered as the first cell of each row. On
 * drop, we PATCH `/(on|off)boarding/tasks/{tid}/reorder` with `{after_task_id}`.
 * Optimistic UI: we splice the item locally so the row snaps into place immediately.
 */
export function SortableTaskList({ tasks, kind = "onboarding", members = [], selection = [], onToggleSelect, onChanged, onReordered }) {
  // Keep a local mirror so the reorder feels instant without waiting for a
  // full journey re-fetch.
  const [order, setOrder] = useState(tasks);
  useEffect(() => { setOrder(tasks); }, [tasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = useMemo(() => order.map((t) => t.id), [order]);

  const handleDragEnd = async (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIdx = order.findIndex((t) => t.id === active.id);
    const newIdx = order.findIndex((t) => t.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(order, oldIdx, newIdx);
    setOrder(next);
    const afterTaskId = newIdx === 0 ? null : next[newIdx - 1].id;
    const url = kind === "onboarding"
      ? `/onboarding/tasks/${active.id}/reorder`
      : `/offboarding/tasks/${active.id}/reorder`;
    try {
      await api.patch(url, { after_task_id: afterTaskId });
      await onReordered?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reorder failed"));
      // Roll back the optimistic update.
      setOrder(tasks);
    }
  };

  if (order.length === 0) return null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {order.map((t) => (
          <SortableRow
            key={t.id}
            task={t}
            members={members}
            kind={kind}
            selected={selection.includes(t.id)}
            onToggleSelect={onToggleSelect}
            onChanged={onChanged}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({ task, members, kind, selected, onToggleSelect, onChanged }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className={`relative group ${isDragging ? "z-10 shadow-md" : ""}`} data-testid={`sortable-row-${task.id}`}>
      <button
        type="button"
        aria-label="Drag to reorder"
        className="absolute left-0.5 top-2 h-6 w-4 flex items-center justify-center opacity-0 group-hover:opacity-70 hover:!opacity-100 cursor-grab active:cursor-grabbing touch-none"
        data-testid={`drag-handle-${task.id}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-3 h-3 text-muted-foreground" />
      </button>
      <OnboardingTaskItem
        task={task}
        members={members}
        kind={kind}
        selectable
        selected={selected}
        onToggleSelect={onToggleSelect}
        onChanged={onChanged}
      />
    </div>
  );
}



function NewTaskEditor({ kind, category, members, onCreate, onClose }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ownerRole, setOwnerRole] = useState("hr");
  const [dueDate, setDueDate] = useState("");
  const [approver, setApprover] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [assignee, setAssignee] = useState("");
  const [isRequired, setIsRequired] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [addToTemplate, setAddToTemplate] = useState(false);
  const [saving, setSaving] = useState(false);
  const memberList = members.map((m) => ({
    id: m.user_id || m.id,
    name: m.user_name || m.name || m.email,
  }));
  const save = async () => {
    setSaving(true);
    try {
      await onCreate({
        title: title.trim(),
        description: description.trim(),
        owner_role: ownerRole,
        due_date: dueDate || null,
        approver_user_id: approver || null,
        reviewer_user_id: reviewer || null,
        assignee_user_id: assignee || null,
        is_required: isRequired,
        is_visible_to_employee: isVisible,
        add_to_template: addToTemplate,
      });
    } catch { /* toast fired by caller */ }
    finally { setSaving(false); }
  };
  return (
    <div className="border-2 border-primary" data-testid="new-task-editor">
      <div className="px-3 py-2 border-b border-border bg-primary/10 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest">
          Add task · <span className="capitalize">{category.replace(/_/g, " ")}</span>
        </div>
        <button className="text-muted-foreground hover:text-foreground" onClick={onClose}><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="p-3 space-y-2 max-h-[70vh] overflow-auto">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Title</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-none h-8 text-sm" placeholder="e.g. Set up laptop encryption" autoFocus data-testid="new-task-title" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Description</label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="rounded-none text-sm resize-none" data-testid="new-task-desc" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Owner role</label>
            <Select value={ownerRole} onValueChange={setOwnerRole}>
              <SelectTrigger className="rounded-none h-8 text-xs" data-testid="new-task-owner"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OWNER_ROLES.map((r) => <SelectItem key={r} value={r} className="text-xs capitalize">{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Due date</label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded-none h-8 text-sm" data-testid="new-task-due" />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Assignee (optional · any workspace user)</label>
          <SearchableSelect
            value={assignee}
            onValueChange={(v) => setAssignee(v || "")}
            options={[{ value: "", label: "— none —" }, ...memberList.map((m) => ({ value: m.id, label: m.name }))]}
            placeholder="Pick a specific person"
            clearable
            triggerClassName="rounded-none h-8 text-xs"
            data-testid="new-task-assignee"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Reviewer</label>
          <SearchableSelect
            value={reviewer}
            onValueChange={(v) => setReviewer(v || "")}
            options={[{ value: "", label: "— none —" }, ...memberList.map((m) => ({ value: m.id, label: m.name }))]}
            placeholder="Select reviewer"
            clearable
            triggerClassName="rounded-none h-8 text-xs"
            data-testid="new-task-reviewer"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Approver</label>
          <SearchableSelect
            value={approver}
            onValueChange={(v) => setApprover(v || "")}
            options={[{ value: "", label: "— none —" }, ...memberList.map((m) => ({ value: m.id, label: m.name }))]}
            placeholder="Select approver"
            clearable
            triggerClassName="rounded-none h-8 text-xs"
            data-testid="new-task-approver"
          />
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} data-testid="new-task-required" />
          Required to complete
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={isVisible} onChange={(e) => setIsVisible(e.target.checked)} data-testid="new-task-visible" />
          Visible to the employee
        </label>
        {kind === "onboarding" && (
          <label className="flex items-center gap-2 text-xs cursor-pointer border-t border-border pt-2">
            <input type="checkbox" checked={addToTemplate} onChange={(e) => setAddToTemplate(e.target.checked)} data-testid="new-task-add-template" />
            Also add to workspace template (future new hires inherit this)
          </label>
        )}
      </div>
      <div className="px-3 py-2 border-t border-border flex items-center gap-2 justify-end">
        <Button variant="ghost" size="sm" className="rounded-none h-7" onClick={onClose}>Cancel</Button>
        <Button size="sm" className="rounded-none h-7" onClick={save} disabled={saving || !title.trim()} data-testid="new-task-save">
          {saving ? "Adding…" : "Add task"}
        </Button>
      </div>
    </div>
  );
}


/**
 * TaskCommentsPanel — lightweight in-place comments thread for an
 * onboarding / offboarding task. HR, admins, the assigned reviewer /
 * approver, the reporting manager, and the employee (when the task is
 * visible to them) can post here. Supports @-mentions parsed by the
 * backend.
 */
export function TaskCommentsPanel({ taskId, taskTitle, onCountChange, onClose, endpoint = "onboarding" }) {
  const [rows, setRows] = useState(null);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const listUrl = `/${endpoint}/tasks/${taskId}/comments`;
  const load = async () => {
    try {
      const r = await api.get(listUrl);
      setRows(r.data || []);
      onCountChange?.((r.data || []).length);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load comments")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [taskId]);

  const send = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      await api.post(listUrl, { body: body.trim() });
      setBody("");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Post failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-2 border-primary" data-testid={`task-comments-thread-${taskId}`}>
      <div className="px-3 py-2 border-b border-border bg-muted/40 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest truncate pr-2">
          Comments · <span className="normal-case font-medium truncate">{taskTitle}</span>
        </div>
        <button className="text-muted-foreground hover:text-foreground" onClick={onClose}><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="max-h-[40vh] overflow-auto p-3 space-y-2">
        {rows === null && <div className="text-xs text-muted-foreground italic">Loading…</div>}
        {rows && rows.length === 0 && (
          <div className="text-xs text-muted-foreground italic" data-testid="task-comments-empty">
            No comments yet. Use @name to mention someone.
          </div>
        )}
        {(rows || []).map((c) => (
          <div key={c.id} className="border-l-2 border-primary/30 pl-2 text-xs" data-testid={`task-comment-${c.id}`}>
            <div className="flex items-baseline gap-1.5">
              <span className="font-semibold">{c.author_name}</span>
              <span className="text-muted-foreground text-[10px] font-mono">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="whitespace-pre-wrap mt-0.5">{c.body}</div>
          </div>
        ))}
      </div>
      <div className="border-t border-border p-2 flex items-end gap-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a comment — use @name to mention"
          rows={2}
          className="rounded-none text-sm resize-none flex-1"
          data-testid="task-comment-input"
        />
        <Button size="sm" className="rounded-none h-8" onClick={send} disabled={saving || !body.trim()} data-testid="task-comment-send">
          {saving ? "…" : "Send"}
        </Button>
      </div>
    </div>
  );
}


/**
 * BulkTaskToolbar — sticky bulk-edit toolbar that appears when at least
 * one task is selected in the JourneyDialog / OffboardingDialog. Lets HR
 * apply a common patch across the whole selection in one call.
 */
export function BulkTaskToolbar({ selection, kind = "onboarding", members = [], onDone }) {
  const [busy, setBusy] = useState(false);
  const count = selection.length;
  if (count === 0) return null;
  const bulkUrl = kind === "onboarding" ? "/onboarding/tasks/bulk" : "/onboarding/tasks/bulk?is_offboarding=true";
  const bulkDeleteUrl = kind === "onboarding" ? "/onboarding/tasks/bulk-delete" : "/onboarding/tasks/bulk-delete?is_offboarding=true";
  const apply = async (patch) => {
    setBusy(true);
    try {
      await api.post(bulkUrl, { ids: selection, patch });
      toast.success(`${count} task${count === 1 ? "" : "s"} updated`);
      await onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk update failed")); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${count} selected custom task(s)? Seeded tasks are skipped.`)) return;
    setBusy(true);
    try {
      const r = await api.post(bulkDeleteUrl, { ids: selection });
      toast.success(`${r.data.deleted} deleted, ${r.data.skipped} skipped (non-custom)`);
      await onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setBusy(false); }
  };

  const memberList = members.map((m) => ({
    id: m.user_id || m.id,
    name: m.user_name || m.name || m.email,
  }));

  return (
    <div className="sticky bottom-0 z-10 border-2 border-primary bg-background shadow-lg p-2 mt-3 flex flex-wrap items-center gap-2" data-testid="bulk-toolbar">
      <span className="text-xs font-semibold text-primary pr-1">{count} selected</span>

      <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" disabled={busy} onClick={() => apply({ status: "done" })} data-testid="bulk-mark-done">
        <Check className="w-3 h-3 mr-1" /> Mark done
      </Button>
      <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" disabled={busy} onClick={() => apply({ status: "skipped" })} data-testid="bulk-mark-skipped">
        Skip
      </Button>
      <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" disabled={busy} onClick={() => apply({ is_visible_to_employee: true })} data-testid="bulk-show">
        <Eye className="w-3 h-3 mr-1" /> Show to employee
      </Button>
      <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" disabled={busy} onClick={() => apply({ is_visible_to_employee: false })} data-testid="bulk-hide">
        <EyeOff className="w-3 h-3 mr-1" /> Hide from employee
      </Button>
      <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" disabled={busy} onClick={() => apply({ is_required: true })} data-testid="bulk-required">
        Mark required
      </Button>

      <BulkDatePicker onApply={(d) => apply({ due_date: d })} disabled={busy} />
      <BulkSelectPicker
        label="Owner"
        options={OWNER_ROLES.map((r) => ({ value: r, label: r }))}
        onApply={(v) => apply({ owner_role: v })}
        disabled={busy}
        testid="bulk-owner"
      />
      <BulkSelectPicker
        label="Category"
        options={(kind === "onboarding" ? ONBOARDING_CATEGORIES : OFFBOARDING_CATEGORIES).map((c) => ({ value: c, label: c.replace(/_/g, " ") }))}
        onApply={(v) => apply({ category: v })}
        disabled={busy}
        testid="bulk-category"
      />
      {memberList.length > 0 && (
        <>
          <BulkSelectPicker
            label="Assignee"
            options={[{ value: "__none__", label: "— none —" }].concat(memberList.map((m) => ({ value: m.id, label: m.name })))}
            onApply={(v) => apply({ assignee_user_id: v === "__none__" ? null : v })}
            disabled={busy}
            testid="bulk-assignee"
          />
          <BulkSelectPicker
            label="Reviewer"
            options={[{ value: "__none__", label: "— none —" }].concat(memberList.map((m) => ({ value: m.id, label: m.name })))}
            onApply={(v) => apply({ reviewer_user_id: v === "__none__" ? null : v })}
            disabled={busy}
            testid="bulk-reviewer"
          />
          <BulkSelectPicker
            label="Approver"
            options={[{ value: "__none__", label: "— none —" }].concat(memberList.map((m) => ({ value: m.id, label: m.name })))}
            onApply={(v) => apply({ approver_user_id: v === "__none__" ? null : v })}
            disabled={busy}
            testid="bulk-approver"
          />
        </>
      )}
      <Button size="sm" variant="outline" className="rounded-none h-7 text-xs text-rose-600 hover:bg-rose-50" disabled={busy} onClick={remove} data-testid="bulk-delete">
        <Trash2 className="w-3 h-3 mr-1" /> Delete custom
      </Button>
    </div>
  );
}

function BulkDatePicker({ onApply, disabled }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState("");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" disabled={disabled} data-testid="bulk-due">Due date…</Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 rounded-none p-2" side="top" align="start">
        <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-none h-8 text-sm" data-testid="bulk-due-input" />
        <Button size="sm" className="rounded-none h-7 mt-2 w-full" disabled={!d} onClick={() => { onApply(d); setOpen(false); }} data-testid="bulk-due-apply">Apply</Button>
      </PopoverContent>
    </Popover>
  );
}

function BulkSelectPicker({ label, options, onApply, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState("");
  const isPeople = options.length > 12;    // heuristic — people lists get the searchable picker
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" disabled={disabled} data-testid={testid}>{label}…</Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 rounded-none p-2" side="top" align="start">
        {isPeople ? (
          <SearchableSelect
            value={v}
            onValueChange={setV}
            options={options}
            placeholder={`Search ${label.toLowerCase()}…`}
            clearable
            triggerClassName="rounded-none h-8 text-xs"
            data-testid={`${testid}-select`}
          />
        ) : (
          <Select value={v} onValueChange={setV}>
            <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`${testid}-select`}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
            <SelectContent>
              {options.map((o) => <SelectItem key={o.value} value={o.value} className="text-xs capitalize">{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button size="sm" className="rounded-none h-7 mt-2 w-full" disabled={!v} onClick={() => { onApply(v); setOpen(false); }} data-testid={`${testid}-apply`}>Apply</Button>
      </PopoverContent>
    </Popover>
  );
}
